/**
 * TASK-ATTR-010: Effect Sprite Factory
 *
 * Factory for creating PIXI sprites from base64-encoded PNG frames.
 * Handles texture caching and sprite lifecycle management.
 *
 * NOTE: Updated for PixiJS v8 compatibility - textures must be created
 * from loaded HTMLImageElements rather than raw data URLs.
 */

import * as PIXI from 'pixi.js';

/**
 * Factory for creating sprites from base64-encoded image data
 * Manages texture cache to avoid re-creating textures from the same base64 data
 */
export class EffectSpriteFactory {
  private textureCache = new Map<string, PIXI.Texture>();
  private pendingLoads = new Map<string, Promise<PIXI.Texture>>();

  /**
   * Load an image from a data URL and return the HTMLImageElement
   */
  private loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(new Error(`Failed to load image: ${e}`));
      img.src = dataUrl;
    });
  }

  /**
   * Create a PIXI texture from base64-encoded PNG data (async for PixiJS v8)
   * Caches textures by their base64 string to avoid duplicate texture creation
   *
   * @param base64 - Base64-encoded PNG string (with or without data URL prefix)
   * @returns Promise<PIXI.Texture>
   */
  async createTextureFromBase64Async(base64: string): Promise<PIXI.Texture> {
    // Check cache first
    const cached = this.textureCache.get(base64);
    if (cached) {
      return cached;
    }

    // Check if already loading
    const pending = this.pendingLoads.get(base64);
    if (pending) {
      return pending;
    }

    // Create loading promise
    const loadPromise = (async () => {
      // Create data URL from base64
      const dataUrl = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;

      // Load image element first (required for PixiJS v8)
      const img = await this.loadImage(dataUrl);

      // Create texture from loaded image
      const texture = PIXI.Texture.from(img);

      // Cache the texture
      this.textureCache.set(base64, texture);
      this.pendingLoads.delete(base64);

      return texture;
    })();

    this.pendingLoads.set(base64, loadPromise);
    return loadPromise;
  }

  /**
   * Create a PIXI texture from base64-encoded PNG data (sync version with fallback)
   * Uses cached textures or creates a placeholder for PixiJS v8 compatibility
   *
   * @param base64 - Base64-encoded PNG string (without data URL prefix)
   * @returns PIXI.Texture (may be placeholder if not pre-loaded)
   */
  createTextureFromBase64(base64: string): PIXI.Texture {
    // Check cache first
    const cached = this.textureCache.get(base64);
    if (cached) {
      return cached;
    }

    // For PixiJS v8, we need to create the texture asynchronously
    // Return a white placeholder and trigger async load
    const dataUrl = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;

    // Create texture synchronously - this triggers the PixiJS v8 warning
    // but should still work for immediate display needs
    const texture = PIXI.Texture.from(dataUrl);

    // Cache it
    this.textureCache.set(base64, texture);

    return texture;
  }

  /**
   * Pre-load all effect frames for a unit (call during texture preloading phase)
   * This ensures textures are ready for synchronous use during combat
   *
   * @param frames - Array of base64-encoded PNG strings
   */
  async preloadFrames(frames: string[]): Promise<void> {
    await Promise.all(frames.map(frame => this.createTextureFromBase64Async(frame)));
  }

  /**
   * Create an animated sprite from an array of base64-encoded frames
   *
   * @param frames - Array of base64-encoded PNG strings
   * @param frameRate - Frames per second (default: 12 FPS)
   * @returns PIXI.AnimatedSprite
   */
  createAnimatedSprite(frames: string[], frameRate: number = 12): PIXI.AnimatedSprite {
    // Convert base64 frames to textures
    const textures = frames.map(frame => this.createTextureFromBase64(frame));

    // Create animated sprite
    const sprite = new PIXI.AnimatedSprite(textures);

    // Set animation speed (convert FPS to PixiJS animationSpeed)
    // animationSpeed = FPS / 60 (PixiJS runs at 60 FPS)
    sprite.animationSpeed = frameRate / 60;

    // Default: don't loop (effect plays once)
    sprite.loop = false;

    return sprite;
  }

  /**
   * Create an animated sprite from pre-loaded frames (async version)
   * Use this for guaranteed texture loading before sprite creation
   *
   * @param frames - Array of base64-encoded PNG strings
   * @param frameRate - Frames per second (default: 12 FPS)
   * @returns Promise<PIXI.AnimatedSprite>
   */
  async createAnimatedSpriteAsync(frames: string[], frameRate: number = 12): Promise<PIXI.AnimatedSprite> {
    // Pre-load all frames first
    const textures = await Promise.all(
      frames.map(frame => this.createTextureFromBase64Async(frame))
    );

    // Create animated sprite with loaded textures
    const sprite = new PIXI.AnimatedSprite(textures);

    // Set animation speed (convert FPS to PixiJS animationSpeed)
    sprite.animationSpeed = frameRate / 60;

    // Default: don't loop (effect plays once)
    sprite.loop = false;

    return sprite;
  }

  /**
   * Dispose of a sprite and clean up its resources
   * Note: This does NOT destroy cached textures (they may be reused)
   *
   * @param sprite - Sprite to dispose
   */
  disposeSprite(sprite: PIXI.Sprite): void {
    // Remove from parent container if attached
    if (sprite.parent) {
      sprite.parent.removeChild(sprite);
    }

    // Destroy the sprite (but not the texture - it may be cached)
    sprite.destroy({ texture: false });
  }

  /**
   * Clear the texture cache and destroy all cached textures
   * WARNING: This will invalidate all sprites created from cached textures
   * Only call this during full cleanup (e.g., when leaving combat)
   */
  clearCache(): void {
    // Destroy all cached textures (with safety checks for already-destroyed textures)
    Array.from(this.textureCache.values()).forEach(texture => {
      try {
        // Check if texture is valid and not already destroyed
        if (texture && !texture.destroyed && texture.source && !texture.source.destroyed) {
          texture.destroy(true);
        }
      } catch (e) {
        // Silently ignore errors during cleanup - texture may already be invalid
        console.warn('[EffectSpriteFactory] Error destroying texture during cleanup:', e);
      }
    });

    // Clear the cache
    this.textureCache.clear();

    // Also clear any pending loads
    this.pendingLoads.clear();
  }

  /**
   * Get cache statistics (for debugging/monitoring)
   */
  getCacheStats() {
    return {
      cachedTextures: this.textureCache.size
    };
  }
}
